import React, { useState, useEffect, useRef } from 'react';
import { api } from '../../App';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import { toast } from 'sonner';
import { 
  Lock, Unlock, Plus, Trash2, Package, Zap, Truck, Settings,
  AlertTriangle, FileDown, Upload, Check, X, Edit2, Search,
  Clock, Wrench, Recycle, Calendar, Activity, RotateCcw, ChevronDown, ChevronUp
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Category configurations with their specific fields and units
const categoryConfig = {
  bom: { 
    label: 'Bill of Materials', 
    icon: Package, 
    color: 'bg-blue-500/20 text-blue-600 border-blue-500/30',
    bgColor: 'bg-blue-50',
    columns: [
      { key: 'name', label: 'Material/Component', type: 'dataset-search', placeholder: 'Search dataset...', required: true },
      { key: 'quantity', label: 'Quantity', type: 'number', placeholder: '0', required: true, align: 'right' },
      { key: 'unit', label: 'Unit', type: 'select', options: ['kg', 'pcs', 'm²', 'm', 'L', 'g'], required: true },
      { key: 'geography', label: 'Geography', type: 'text', placeholder: 'Global' },
      { key: 'quality', label: 'Quality', type: 'quality' }
    ]
  },
  energy: { 
    label: 'Energy Inputs', 
    icon: Zap, 
    color: 'bg-amber-500/20 text-amber-600 border-amber-500/30',
    bgColor: 'bg-amber-50',
    columns: [
      { key: 'name', label: 'Energy Source', type: 'dataset-search', placeholder: 'Search dataset...', required: true },
      { key: 'quantity', label: 'Quantity', type: 'number', placeholder: '0', required: true, align: 'right' },
      { key: 'unit', label: 'Unit', type: 'select', options: ['kWh', 'MJ', 'm³', 'L', 'kg'], required: true },
      { key: 'geography', label: 'Geography', type: 'text', placeholder: 'Global' },
      { key: 'quality', label: 'Quality', type: 'quality' }
    ]
  },
  transport: { 
    label: 'Transport', 
    icon: Truck, 
    color: 'bg-emerald-500/20 text-emerald-600 border-emerald-500/30',
    bgColor: 'bg-emerald-50',
    columns: [
      { key: 'name', label: 'Transport Type', type: 'dataset-search', placeholder: 'Search dataset...', required: true },
      { key: 'distance_km', label: 'Distance (km)', type: 'number', placeholder: '0', required: true, align: 'right' },
      { key: 'load_kg', label: 'Load (kg)', type: 'number', placeholder: '0', required: true, align: 'right' },
      { key: 'geography', label: 'Geography', type: 'text', placeholder: 'Global' },
      { key: 'quality', label: 'Quality', type: 'quality' }
    ]
  },
  process: { 
    label: 'Processes', 
    icon: Settings, 
    color: 'bg-purple-500/20 text-purple-600 border-purple-500/30',
    bgColor: 'bg-purple-50',
    columns: [
      { key: 'name', label: 'Process Name', type: 'dataset-search', placeholder: 'Search dataset...', required: true },
      { key: 'quantity', label: 'Quantity', type: 'number', placeholder: '0', required: true, align: 'right' },
      { key: 'unit', label: 'Unit', type: 'select', options: ['m', 'kg', 'm²', 'hours', 'pcs'], required: true },
      { key: 'geography', label: 'Geography', type: 'text', placeholder: 'Global' },
      { key: 'quality', label: 'Quality', type: 'quality' },
      { key: 'yield_percent', label: 'Yield %', type: 'number', placeholder: '100', align: 'right' },
      { key: 'scrap_percent', label: 'Scrap %', type: 'number', placeholder: '0', align: 'right' }
    ]
  }
};

// Gate-to-Grave category configurations (conditionally shown)
const g2gCategoryConfig = {
  use_phase: {
    label: 'Use Phase',
    icon: Activity,
    color: 'bg-cyan-500/20 text-cyan-600 border-cyan-500/30',
    bgColor: 'bg-cyan-50',
    columns: [
      { key: 'name', label: 'Resource/Consumable', type: 'dataset-search', placeholder: 'Search dataset...', required: true },
      { key: 'quantity', label: 'Quantity', type: 'number', placeholder: '0', required: true, align: 'right' },
      { key: 'unit', label: 'Unit', type: 'select', options: ['kWh', 'L', 'm³', 'kg', 'pcs'], required: true },
      { key: 'usage_basis', label: 'Per', type: 'select', options: ['per_year', 'per_hour', 'per_cycle'], required: true },
      { key: 'geography', label: 'Geography', type: 'text', placeholder: 'Global' },
      { key: 'quality', label: 'Quality', type: 'quality' }
    ]
  },
  maintenance: {
    label: 'Maintenance & Replacement',
    icon: Wrench,
    color: 'bg-orange-500/20 text-orange-600 border-orange-500/30',
    bgColor: 'bg-orange-50',
    columns: [
      { key: 'name', label: 'Spare Part/Component', type: 'dataset-search', placeholder: 'Search dataset...', required: true },
      { key: 'component_name', label: 'Component', type: 'text', placeholder: 'e.g., Battery, Filter' },
      { key: 'quantity', label: 'Qty per Replacement', type: 'number', placeholder: '0', required: true, align: 'right' },
      { key: 'unit', label: 'Unit', type: 'select', options: ['kg', 'pcs', 'L', 'm'], required: true },
      { key: 'replacement_interval_years', label: 'Interval (years)', type: 'number', placeholder: '0', required: true, align: 'right' },
      { key: 'quality', label: 'Quality', type: 'quality' }
    ]
  },
  eol: {
    label: 'End-of-Life',
    icon: Recycle,
    color: 'bg-rose-500/20 text-rose-600 border-rose-500/30',
    bgColor: 'bg-rose-50',
    columns: [
      { key: 'name', label: 'Process/Treatment', type: 'dataset-search', placeholder: 'Search dataset...', required: true },
      { key: 'quantity', label: 'Quantity', type: 'number', placeholder: '0', required: true, align: 'right' },
      { key: 'unit', label: 'Unit', type: 'select', options: ['kg', 'tkm', 'pcs'], required: true },
      { key: 'eol_method', label: 'Method', type: 'select', options: ['cut_off', 'substitution', 'allocation'] },
      { key: 'geography', label: 'Geography', type: 'text', placeholder: 'Global' },
      { key: 'quality', label: 'Quality', type: 'quality' }
    ]
  }
};

const qualityOptions = ['high', 'medium', 'proxy'];

// Dataset Search Autocomplete Component
function DatasetAutocomplete({ datasets, value, onChange, placeholder, category }) {
  const [search, setSearch] = useState(value || '');
  const [isOpen, setIsOpen] = useState(false);
  const [filteredDatasets, setFilteredDatasets] = useState([]);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    setSearch(value || '');
  }, [value]);

  useEffect(() => {
    if (search.length > 0) {
      const filtered = datasets
        .filter(ds => ds.category === category)
        .filter(ds => ds.name.toLowerCase().includes(search.toLowerCase()))
        .slice(0, 10);
      setFilteredDatasets(filtered);
    } else {
      setFilteredDatasets(datasets.filter(ds => ds.category === category).slice(0, 10));
    }
  }, [search, datasets, category]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target) &&
          inputRef.current && !inputRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (dataset) => {
    setSearch(dataset.name);
    onChange(dataset);
    setIsOpen(false);
  };

  const handleInputChange = (e) => {
    setSearch(e.target.value);
    setIsOpen(true);
    // If user clears or changes input, clear the selection
    if (e.target.value !== value) {
      onChange({ name: e.target.value, id: null, emission_factor: null });
    }
  };

  return (
    <div className="relative">
      <div className="relative">
        <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
        <Input
          ref={inputRef}
          type="text"
          value={search}
          onChange={handleInputChange}
          onFocus={() => setIsOpen(true)}
          placeholder={placeholder}
          className="h-8 text-sm pl-7 bg-white"
          data-testid="dataset-search-input"
        />
      </div>
      
      {isOpen && (
        <div 
          ref={dropdownRef}
          className="absolute z-[100] w-72 mt-1 bg-white border border-slate-200 rounded-md shadow-xl max-h-60 overflow-auto"
          style={{ left: 0, top: '100%' }}
        >
          {filteredDatasets.length > 0 ? (
            filteredDatasets.map((ds) => (
              <button
                key={ds.id}
                onClick={() => handleSelect(ds)}
                className="w-full px-3 py-2 text-left text-sm hover:bg-sky-50 flex items-center justify-between border-b border-slate-100 last:border-b-0"
                data-testid={`dataset-option-${ds.id}`}
              >
                <div>
                  <div className="font-medium text-slate-900">{ds.name}</div>
                  <div className="text-xs text-slate-500">{ds.source} • {ds.geography}</div>
                </div>
                <div className="text-xs font-mono text-emerald-700 bg-emerald-50 px-2 py-1 rounded">
                  EF: {ds.emission_factor}
                </div>
              </button>
            ))
          ) : (
            <div className="px-3 py-4 text-sm text-slate-500 text-center">
              No matching datasets found for "{search}"
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function Phase2Inventory({ project, onUpdate, isLocked }) {
  const [loading, setLoading] = useState(true);
  const [inventory, setInventory] = useState([]);
  const [datasets, setDatasets] = useState([]);
  const [activeTab, setActiveTab] = useState('bom');
  const [locking, setLocking] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editingRowId, setEditingRowId] = useState(null);
  const [editingData, setEditingData] = useState({});
  const [newRow, setNewRow] = useState(null);
  const fileInputRef = useRef(null);
  
  // Gate-to-Grave state
  const [goalScope, setGoalScope] = useState(null);
  const [g2gSettings, setG2gSettings] = useState(null);
  const [g2gSummary, setG2gSummary] = useState(null);
  const [showG2gSection, setShowG2gSection] = useState(false);
  const [savingG2gSettings, setSavingG2gSettings] = useState(false);
  const [productLifetime, setProductLifetime] = useState('');
  const [lifetimeGeography, setLifetimeGeography] = useState('Global');
  
  // Check if system boundary is cradle_to_grave
  const isCradleToGrave = goalScope?.system_boundary === 'cradle_to_grave';

  useEffect(() => {
    fetchData();
  }, [project.id]);

  const fetchData = async () => {
    try {
      const [invResponse, dsResponse, gsResponse] = await Promise.all([
        api.get(`/projects/${project.id}/inventory`),
        api.get('/datasets'),
        api.get(`/projects/${project.id}/goal-scope`).catch(() => ({ data: null }))
      ]);
      setInventory(invResponse.data);
      setDatasets(dsResponse.data);
      setGoalScope(gsResponse.data);
      
      // If cradle_to_grave, fetch G2G settings and summary
      if (gsResponse.data?.system_boundary === 'cradle_to_grave') {
        try {
          const [settingsRes, summaryRes] = await Promise.all([
            api.get(`/projects/${project.id}/gate-to-grave-settings`).catch(() => ({ data: null })),
            api.get(`/projects/${project.id}/gate-to-grave-summary`).catch(() => ({ data: null }))
          ]);
          if (settingsRes.data) {
            setG2gSettings(settingsRes.data);
            setProductLifetime(settingsRes.data.product_lifetime_years?.toString() || '');
            setLifetimeGeography(settingsRes.data.lifetime_geography || 'Global');
          }
          if (summaryRes.data) {
            setG2gSummary(summaryRes.data);
          }
        } catch (e) {
          // G2G settings might not exist yet
        }
      }
    } catch (error) {
      toast.error('Failed to load inventory');
    } finally {
      setLoading(false);
    }
  };

  // Get the appropriate category config based on active tab
  const getCategoryConfig = (cat) => {
    if (categoryConfig[cat]) return categoryConfig[cat];
    if (g2gCategoryConfig[cat]) return g2gCategoryConfig[cat];
    return categoryConfig.bom;
  };

  const getEmptyRow = (category) => {
    const config = getCategoryConfig(category);
    const baseRow = {
      name: '',
      dataset_id: null,
      emission_factor: null,
      quantity: '',
      unit: config.columns.find(c => c.key === 'unit')?.options?.[0] || '',
      geography: 'Global',
      data_quality: 'medium',
      distance_km: '',
      load_kg: '',
      yield_percent: '',
      scrap_percent: ''
    };
    
    // Add G2G specific fields
    if (category === 'use_phase') {
      baseRow.usage_basis = 'per_year';
    } else if (category === 'maintenance') {
      baseRow.component_name = '';
      baseRow.replacement_interval_years = '';
    } else if (category === 'eol') {
      baseRow.eol_method = 'cut_off';
    }
    
    return baseRow;
  };

  const addNewRow = () => {
    setNewRow(getEmptyRow(activeTab));
  };

  const cancelNewRow = () => {
    setNewRow(null);
  };

  const saveNewRow = async () => {
    const config = getCategoryConfig(activeTab);
    
    // Validate required fields
    if (!newRow.name) {
      toast.error('Please select a dataset item');
      return;
    }
    
    if (!newRow.dataset_id) {
      toast.error('Please select a valid item from the dataset library');
      return;
    }

    if (activeTab === 'transport') {
      if (!newRow.distance_km || !newRow.load_kg) {
        toast.error('Distance and Load are required for transport');
        return;
      }
    } else {
      if (!newRow.quantity) {
        toast.error('Quantity is required');
        return;
      }
    }
    
    // G2G specific validations
    if (activeTab === 'maintenance' && !newRow.replacement_interval_years) {
      toast.error('Replacement interval is required for maintenance items');
      return;
    }

    try {
      const payload = {
        category: activeTab,
        name: newRow.name,
        quantity: activeTab === 'transport' 
          ? (parseFloat(newRow.distance_km) * parseFloat(newRow.load_kg)) / 1000 
          : parseFloat(newRow.quantity) || 0,
        unit: activeTab === 'transport' ? 'tkm' : newRow.unit,
        dataset_id: newRow.dataset_id,
        geography: newRow.geography || 'Global',
        data_quality: newRow.data_quality || 'medium',
        data_source: 'generic',
        additional_data: {}
      };

      // Category-specific fields
      if (activeTab === 'transport') {
        payload.additional_data.distance_km = parseFloat(newRow.distance_km) || 0;
        payload.additional_data.load_kg = parseFloat(newRow.load_kg) || 0;
      }
      if (activeTab === 'process') {
        if (newRow.yield_percent) payload.additional_data.yield_percent = parseFloat(newRow.yield_percent);
        if (newRow.scrap_percent) payload.additional_data.scrap_percent = parseFloat(newRow.scrap_percent);
      }
      
      // Gate-to-Grave specific fields
      if (activeTab === 'use_phase') {
        payload.usage_basis = newRow.usage_basis || 'per_year';
      }
      if (activeTab === 'maintenance') {
        payload.component_name = newRow.component_name || '';
        payload.replacement_interval_years = parseFloat(newRow.replacement_interval_years) || 0;
      }
      if (activeTab === 'eol') {
        payload.eol_method = newRow.eol_method || 'cut_off';
      }

      const response = await api.post(`/projects/${project.id}/inventory`, payload);
      setInventory([...inventory, response.data]);
      setNewRow(null);
      toast.success('Item added');
      
      // Refresh G2G summary if applicable
      if (['use_phase', 'maintenance', 'eol'].includes(activeTab)) {
        try {
          const summaryRes = await api.get(`/projects/${project.id}/gate-to-grave-summary`);
          setG2gSummary(summaryRes.data);
        } catch (e) {}
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add item');
    }
  };

  const startEditing = (item) => {
    setEditingRowId(item.id);
    setEditingData({
      name: item.name,
      dataset_id: item.dataset_id,
      emission_factor: item.emission_factor,
      quantity: item.quantity,
      unit: item.unit,
      geography: item.geography || 'Global',
      data_quality: item.data_quality || 'medium',
      distance_km: item.additional_data?.distance_km || '',
      load_kg: item.additional_data?.load_kg || '',
      yield_percent: item.additional_data?.yield_percent || '',
      scrap_percent: item.additional_data?.scrap_percent || ''
    });
  };

  const cancelEditing = () => {
    setEditingRowId(null);
    setEditingData({});
  };

  const saveEditing = async (item) => {
    if (!editingData.name || !editingData.dataset_id) {
      toast.error('Please select a valid item from the dataset library');
      return;
    }

    try {
      const payload = {
        category: item.category,
        name: editingData.name,
        quantity: item.category === 'transport' 
          ? (parseFloat(editingData.distance_km) * parseFloat(editingData.load_kg)) / 1000 
          : parseFloat(editingData.quantity) || 0,
        unit: item.category === 'transport' ? 'tkm' : editingData.unit,
        dataset_id: editingData.dataset_id,
        geography: editingData.geography || 'Global',
        data_quality: editingData.data_quality || 'medium',
        data_source: 'generic',
        additional_data: {}
      };

      if (item.category === 'transport') {
        payload.additional_data.distance_km = parseFloat(editingData.distance_km) || 0;
        payload.additional_data.load_kg = parseFloat(editingData.load_kg) || 0;
      }
      if (item.category === 'process') {
        if (editingData.yield_percent) payload.additional_data.yield_percent = parseFloat(editingData.yield_percent);
        if (editingData.scrap_percent) payload.additional_data.scrap_percent = parseFloat(editingData.scrap_percent);
      }

      const response = await api.put(`/projects/${project.id}/inventory/${item.id}`, payload);
      setInventory(inventory.map(i => i.id === item.id ? response.data : i));
      setEditingRowId(null);
      setEditingData({});
      toast.success('Item updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update item');
    }
  };

  const deleteItem = async (itemId) => {
    try {
      await api.delete(`/projects/${project.id}/inventory/${itemId}`);
      setInventory(inventory.filter(i => i.id !== itemId));
      toast.success('Item deleted');
      
      // Refresh G2G summary if applicable
      const item = inventory.find(i => i.id === itemId);
      if (item && ['use_phase', 'maintenance', 'eol'].includes(item.category)) {
        try {
          const summaryRes = await api.get(`/projects/${project.id}/gate-to-grave-summary`);
          setG2gSummary(summaryRes.data);
        } catch (e) {}
      }
    } catch (error) {
      toast.error('Failed to delete item');
    }
  };

  // Save Gate-to-Grave settings
  const saveG2gSettings = async () => {
    if (!productLifetime || parseFloat(productLifetime) <= 0) {
      toast.error('Product lifetime must be greater than 0');
      return;
    }
    
    setSavingG2gSettings(true);
    try {
      const response = await api.post(`/projects/${project.id}/gate-to-grave-settings`, {
        product_lifetime_years: parseFloat(productLifetime),
        lifetime_geography: lifetimeGeography || 'Global'
      });
      setG2gSettings(response.data);
      toast.success('Product lifetime saved');
      
      // Refresh summary
      const summaryRes = await api.get(`/projects/${project.id}/gate-to-grave-summary`);
      setG2gSummary(summaryRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSavingG2gSettings(false);
    }
  };

  const lockPhase = async () => {
    if (inventory.length === 0) {
      toast.error('Add at least one inventory item before locking');
      return;
    }

    // Check that all items have valid dataset references
    const itemsWithoutDataset = inventory.filter(i => !i.dataset_id || !i.emission_factor);
    if (itemsWithoutDataset.length > 0) {
      toast.error(`${itemsWithoutDataset.length} item(s) are missing dataset references. Please ensure all items are linked to datasets.`);
      return;
    }
    
    // Gate-to-Grave validation
    if (isCradleToGrave) {
      if (!g2gSettings?.product_lifetime_years) {
        toast.error('Product lifetime is required for Gate-to-Grave assessment');
        return;
      }
      
      const usePhaseItems = inventory.filter(i => i.category === 'use_phase');
      const eolItems = inventory.filter(i => i.category === 'eol');
      
      if (usePhaseItems.length === 0) {
        toast.error('At least one Use Phase item is required for Gate-to-Grave assessment');
        return;
      }
      
      if (eolItems.length === 0) {
        toast.error('At least one End-of-Life item is required for Gate-to-Grave assessment');
        return;
      }
    }

    setLocking(true);
    try {
      await api.post(`/projects/${project.id}/inventory/lock`);
      toast.success('Phase 2 locked successfully');
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to lock phase');
    } finally {
      setLocking(false);
    }
  };

  const handleUnlock = async () => {
    if (!window.confirm('Unlocking Phase 2 will reset Phase 3 (LCIA) and Phase 4 (Interpretation) to draft status. LCIA results and AI insights will be deleted. Do you want to continue?')) {
      return;
    }

    setUnlocking(true);
    try {
      await api.post(`/projects/${project.id}/inventory/unlock`);
      toast.success('Phase 2 unlocked. You can now edit inventory data.');
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unlock phase');
    } finally {
      setUnlocking(false);
    }
  };

  const downloadTemplate = (category) => {
    const token = localStorage.getItem('token');
    fetch(`${BACKEND_URL}/api/inventory/template/${category}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(response => response.blob())
      .then(blob => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${category}_template.csv`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success('Template downloaded');
      })
      .catch(() => toast.error('Failed to download template'));
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post(
        `/projects/${project.id}/inventory/upload/${activeTab}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      if (response.data.items_created > 0) {
        toast.success(`${response.data.items_created} items imported`);
        fetchData();
      }
      if (response.data.errors?.length > 0) {
        toast.error(`${response.data.errors.length} errors during import`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload CSV');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const filteredInventory = inventory.filter(item => item.category === activeTab);

  const getQualityBadge = (quality) => {
    const colors = {
      high: 'bg-emerald-100 text-emerald-700',
      medium: 'bg-amber-100 text-amber-700',
      proxy: 'bg-red-100 text-red-700'
    };
    return colors[quality] || colors.medium;
  };

  const getCategoryCounts = () => ({
    bom: inventory.filter(i => i.category === 'bom').length,
    energy: inventory.filter(i => i.category === 'energy').length,
    transport: inventory.filter(i => i.category === 'transport').length,
    process: inventory.filter(i => i.category === 'process').length,
    // Gate-to-Grave categories
    use_phase: inventory.filter(i => i.category === 'use_phase').length,
    maintenance: inventory.filter(i => i.category === 'maintenance').length,
    eol: inventory.filter(i => i.category === 'eol').length
  });

  const counts = getCategoryCounts();

  // Handle dataset selection from autocomplete
  const handleDatasetSelect = (dataset, setData, data) => {
    setData({
      ...data,
      name: dataset.name,
      dataset_id: dataset.id,
      emission_factor: dataset.emission_factor
    });
  };

  // Render a cell based on column type
  const renderCell = (col, value, isEditing, data, setData, item = null) => {
    if (!isEditing) {
      // Display mode
      if (col.type === 'quality') {
        return (
          <span className={`px-2 py-1 rounded text-xs font-medium ${getQualityBadge(value)}`}>
            {value || 'medium'}
          </span>
        );
      }
      if (col.type === 'dataset-search') {
        return (
          <div>
            <span className="font-medium text-slate-900">{value || '-'}</span>
            {item?.emission_factor && (
              <span className="ml-2 text-xs text-slate-500 font-mono">
                (EF: {item.emission_factor})
              </span>
            )}
          </div>
        );
      }
      if (col.align === 'right') {
        return <span className="font-mono text-slate-700">{value || '-'}</span>;
      }
      return <span className="text-slate-700">{value || '-'}</span>;
    }

    // Edit mode
    if (col.type === 'dataset-search') {
      return (
        <DatasetAutocomplete
          datasets={datasets}
          value={data.name || ''}
          category={activeTab}
          placeholder={col.placeholder}
          onChange={(ds) => handleDatasetSelect(ds, setData, data)}
        />
      );
    }
    if (col.type === 'select') {
      return (
        <Select value={data[col.key] || ''} onValueChange={(v) => setData({...data, [col.key]: v})}>
          <SelectTrigger className="h-8 text-sm bg-white">
            <SelectValue placeholder="Select..." />
          </SelectTrigger>
          <SelectContent className="bg-white">
            {col.options.map(opt => (
              <SelectItem key={opt} value={opt}>{opt}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }
    if (col.type === 'quality') {
      return (
        <Select value={data.data_quality || 'medium'} onValueChange={(v) => setData({...data, data_quality: v})}>
          <SelectTrigger className="h-8 text-sm bg-white">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-white">
            {qualityOptions.map(q => (
              <SelectItem key={q} value={q} className="capitalize">{q}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }
    return (
      <Input
        type={col.type === 'number' ? 'number' : 'text'}
        value={data[col.key] || ''}
        onChange={(e) => setData({...data, [col.key]: e.target.value})}
        placeholder={col.placeholder}
        className={`h-8 text-sm bg-white ${col.align === 'right' ? 'text-right' : ''}`}
      />
    );
  };

  // Get display value for a column
  const getDisplayValue = (item, col) => {
    if (col.key === 'quality') return item.data_quality;
    if (col.key === 'distance_km' || col.key === 'load_kg' || 
        col.key === 'yield_percent' || col.key === 'scrap_percent') {
      return item.additional_data?.[col.key];
    }
    // G2G specific fields
    if (col.key === 'usage_basis') {
      const basisMap = { 'per_year': 'Per Year', 'per_hour': 'Per Hour', 'per_cycle': 'Per Cycle' };
      return basisMap[item.usage_basis] || item.usage_basis;
    }
    if (col.key === 'replacement_interval_years') {
      const interval = item.replacement_interval_years;
      const count = item.derived_replacement_count;
      return interval ? `${interval}y (${count || 0} replacements)` : '-';
    }
    if (col.key === 'eol_method') {
      const methodMap = { 'cut_off': 'Cut-off', 'substitution': 'Substitution', 'allocation': 'Allocation' };
      return methodMap[item.eol_method] || item.eol_method || '-';
    }
    return item[col.key];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-sky-600"></div>
      </div>
    );
  }

  const config = getCategoryConfig(activeTab);
  const Icon = config.icon;

  return (
    <div className="space-y-6" data-testid="phase2-inventory">
      {/* Phase Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`p-3 rounded-xl ${isLocked ? 'bg-emerald-100' : 'bg-sky-100'}`}>
            {isLocked ? <Lock className="h-6 w-6 text-emerald-600" /> : <Package className="h-6 w-6 text-sky-600" />}
          </div>
          <div>
            <h2 className="text-2xl font-bold text-slate-900">ISO Phase 2 – Inventory (LCI)</h2>
            <p className="text-slate-500">Quantify all material and energy flows from the dataset library</p>
          </div>
        </div>
        <Badge variant={isLocked ? "default" : "outline"} className={isLocked ? "bg-emerald-600" : "border-slate-300 text-slate-600"}>
          {isLocked ? 'Locked' : inventory.length > 0 ? 'In Progress' : 'Not Started'}
        </Badge>
      </div>

      {/* Dataset Warning */}
      {datasets.length === 0 && (
        <Alert className="border-amber-300 bg-amber-50">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-amber-800">
            No datasets found. Please add datasets to the Dataset Library first before adding inventory items.
          </AlertDescription>
        </Alert>
      )}

      {/* Category Tabs - Cradle to Gate */}
      <div className="flex gap-2 p-1.5 bg-slate-100 rounded-xl">
        {Object.entries(categoryConfig).map(([key, cfg]) => {
          const TabIcon = cfg.icon;
          const count = counts[key];
          const isActive = activeTab === key;
          
          return (
            <button
              key={key}
              onClick={() => { setActiveTab(key); setNewRow(null); setEditingRowId(null); }}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-all ${
                isActive 
                  ? `${cfg.color} border bg-white shadow-sm` 
                  : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
              }`}
              data-testid={`tab-${key}`}
            >
              <TabIcon className="h-4 w-4" />
              <span className="hidden sm:inline">{cfg.label}</span>
              {count > 0 && (
                <span className={`text-xs px-2 py-0.5 rounded-full ${isActive ? 'bg-slate-200' : 'bg-slate-200'}`}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Gate-to-Grave Section - Only shown when system boundary is cradle_to_grave */}
      {isCradleToGrave && (
        <div className="space-y-4">
          {/* G2G Header */}
          <div 
            className="flex items-center justify-between p-4 bg-gradient-to-r from-cyan-50 to-rose-50 rounded-xl border border-cyan-200 cursor-pointer"
            onClick={() => setShowG2gSection(!showG2gSection)}
          >
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-cyan-100">
                <RotateCcw className="h-5 w-5 text-cyan-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900">Gate-to-Grave Assessment</h3>
                <p className="text-sm text-slate-500">Use Phase, Maintenance & End-of-Life</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              {g2gSummary && (
                <div className="flex items-center gap-2 text-sm">
                  <Badge variant={g2gSummary.use_phase?.complete ? "default" : "outline"} className={g2gSummary.use_phase?.complete ? "bg-cyan-600" : ""}>
                    Use: {counts.use_phase}
                  </Badge>
                  <Badge variant={g2gSummary.maintenance?.complete ? "default" : "outline"} className={g2gSummary.maintenance?.complete ? "bg-orange-600" : ""}>
                    Maint: {counts.maintenance}
                  </Badge>
                  <Badge variant={g2gSummary.eol?.complete ? "default" : "outline"} className={g2gSummary.eol?.complete ? "bg-rose-600" : ""}>
                    EoL: {counts.eol}
                  </Badge>
                </div>
              )}
              {showG2gSection ? <ChevronUp className="h-5 w-5 text-slate-400" /> : <ChevronDown className="h-5 w-5 text-slate-400" />}
            </div>
          </div>

          <AnimatePresence>
            {showG2gSection && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="space-y-4"
              >
                {/* Product Lifetime Setting */}
                <Card className="border-cyan-200 bg-cyan-50/30">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-cyan-600" />
                      Product Lifetime (Required)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-4">
                      <div className="flex-1">
                        <label className="text-sm text-slate-600 mb-1 block">Lifetime (years)</label>
                        <Input
                          type="number"
                          value={productLifetime}
                          onChange={(e) => setProductLifetime(e.target.value)}
                          placeholder="e.g., 15"
                          className="max-w-xs"
                          disabled={isLocked}
                          data-testid="product-lifetime-input"
                        />
                      </div>
                      <div className="flex-1">
                        <label className="text-sm text-slate-600 mb-1 block">Geography</label>
                        <Select value={lifetimeGeography} onValueChange={setLifetimeGeography} disabled={isLocked}>
                          <SelectTrigger className="max-w-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Global">Global</SelectItem>
                            <SelectItem value="India">India</SelectItem>
                            <SelectItem value="EU">EU</SelectItem>
                            <SelectItem value="US">US</SelectItem>
                            <SelectItem value="China">China</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {!isLocked && (
                        <Button onClick={saveG2gSettings} disabled={savingG2gSettings} className="mt-5 bg-cyan-600 hover:bg-cyan-700">
                          {savingG2gSettings ? 'Saving...' : 'Save Lifetime'}
                        </Button>
                      )}
                    </div>
                    {g2gSettings?.product_lifetime_years && (
                      <p className="text-sm text-emerald-600 mt-2 flex items-center gap-1">
                        <Check className="h-4 w-4" /> Lifetime set to {g2gSettings.product_lifetime_years} years
                      </p>
                    )}
                  </CardContent>
                </Card>

                {/* G2G Category Tabs */}
                <div className="flex gap-2 p-1.5 bg-gradient-to-r from-cyan-100 to-rose-100 rounded-xl">
                  {Object.entries(g2gCategoryConfig).map(([key, cfg]) => {
                    const TabIcon = cfg.icon;
                    const count = counts[key];
                    const isActive = activeTab === key;
                    
                    return (
                      <button
                        key={key}
                        onClick={() => { setActiveTab(key); setNewRow(null); setEditingRowId(null); }}
                        className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-all ${
                          isActive 
                            ? `${cfg.color} border bg-white shadow-sm` 
                            : 'text-slate-500 hover:text-slate-700 hover:bg-white/50'
                        }`}
                        data-testid={`tab-${key}`}
                      >
                        <TabIcon className="h-4 w-4" />
                        <span className="hidden sm:inline">{cfg.label}</span>
                        {count > 0 && (
                          <span className={`text-xs px-2 py-0.5 rounded-full ${isActive ? 'bg-slate-200' : 'bg-slate-200'}`}>
                            {count}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}

      {/* Active Category Content */}
      <Card className={`border ${config.bgColor} shadow-sm`}>
        <CardHeader className="pb-4 bg-white rounded-t-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${config.color}`}>
                <Icon className="h-5 w-5" />
              </div>
              <div>
                <CardTitle className="text-lg text-slate-900">{config.label}</CardTitle>
                <p className="text-sm text-slate-500">{filteredInventory.length} items • Select from dataset library</p>
              </div>
            </div>
            
            {!isLocked && (
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => downloadTemplate(activeTab)} data-testid="download-template-btn" className="border-slate-300">
                  <FileDown className="h-4 w-4 mr-2" />
                  Template
                </Button>
                
                <input type="file" ref={fileInputRef} accept=".csv" onChange={handleFileUpload} className="hidden" />
                <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="upload-csv-btn" className="border-slate-300">
                  {uploading ? <div className="h-4 w-4 mr-2 animate-spin rounded-full border-2 border-current border-t-transparent" /> : <Upload className="h-4 w-4 mr-2" />}
                  {uploading ? 'Uploading...' : 'Upload CSV'}
                </Button>
              </div>
            )}
          </div>
        </CardHeader>

        <CardContent className="bg-white rounded-b-lg overflow-visible">
          {/* Dynamic Table based on category */}
          <div className="rounded-lg border border-slate-200">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  {config.columns.map((col) => (
                    <th 
                      key={col.key} 
                      className={`px-3 py-3 font-semibold text-slate-600 whitespace-nowrap ${col.align === 'right' ? 'text-right' : 'text-left'}`}
                    >
                      {col.label}
                      {col.required && <span className="text-red-500 ml-1">*</span>}
                    </th>
                  ))}
                  {!isLocked && <th className="px-3 py-3 w-24 text-right text-slate-600 font-semibold">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {/* Existing rows */}
                {filteredInventory.map((item) => {
                  const isEditing = editingRowId === item.id;
                  return (
                    <tr key={item.id} className={`border-b border-slate-100 ${isEditing ? 'bg-sky-50' : 'hover:bg-slate-50'}`}>
                      {config.columns.map((col) => (
                        <td key={col.key} className={`px-3 py-2 ${col.align === 'right' ? 'text-right' : ''} ${col.type === 'dataset-search' ? 'relative overflow-visible' : ''}`}>
                          {renderCell(
                            col, 
                            getDisplayValue(item, col), 
                            isEditing, 
                            editingData, 
                            setEditingData,
                            item
                          )}
                        </td>
                      ))}
                      {!isLocked && (
                        <td className="px-3 py-2 text-right">
                          {isEditing ? (
                            <div className="flex items-center justify-end gap-1">
                              <Button size="icon" variant="ghost" onClick={() => saveEditing(item)} className="h-7 w-7 text-emerald-600 hover:bg-emerald-50">
                                <Check className="h-4 w-4" />
                              </Button>
                              <Button size="icon" variant="ghost" onClick={cancelEditing} className="h-7 w-7 text-slate-500 hover:bg-slate-100">
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          ) : (
                            <div className="flex items-center justify-end gap-1">
                              <Button size="icon" variant="ghost" onClick={() => startEditing(item)} className="h-7 w-7 text-slate-500 hover:bg-slate-100">
                                <Edit2 className="h-4 w-4" />
                              </Button>
                              <Button size="icon" variant="ghost" onClick={() => deleteItem(item.id)} className="h-7 w-7 text-red-500 hover:bg-red-50">
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}

                {/* New row being added */}
                {newRow && (
                  <tr className="bg-emerald-50 border-b border-emerald-200">
                    {config.columns.map((col) => (
                      <td key={col.key} className={`px-3 py-2 ${col.align === 'right' ? 'text-right' : ''} ${col.type === 'dataset-search' ? 'relative overflow-visible' : ''}`}>
                        {renderCell(col, newRow[col.key], true, newRow, setNewRow)}
                      </td>
                    ))}
                    <td className="px-3 py-2 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button size="icon" variant="ghost" onClick={saveNewRow} className="h-7 w-7 text-emerald-600 hover:bg-emerald-100">
                          <Check className="h-4 w-4" />
                        </Button>
                        <Button size="icon" variant="ghost" onClick={cancelNewRow} className="h-7 w-7 text-slate-500 hover:bg-slate-100">
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                )}

                {/* Empty state */}
                {filteredInventory.length === 0 && !newRow && (
                  <tr>
                    <td colSpan={config.columns.length + (isLocked ? 0 : 1)} className="px-4 py-8 text-center text-slate-500">
                      No items added yet. {!isLocked && 'Click "Add Item" to get started.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Add Item Button */}
          {!isLocked && !newRow && datasets.filter(ds => ds.category === activeTab).length > 0 && (
            <div className="mt-4">
              <Button variant="outline" onClick={addNewRow} data-testid="add-item-btn" className="w-full border-dashed border-slate-300 text-slate-600 hover:bg-slate-50">
                <Plus className="h-4 w-4 mr-2" />
                Add Item from Dataset Library
              </Button>
            </div>
          )}

          {!isLocked && datasets.filter(ds => ds.category === activeTab).length === 0 && (
            <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg text-center">
              <p className="text-amber-800 text-sm">
                No datasets available for {config.label}. Please add datasets to the Dataset Library first.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Lock Phase Button */}
      {!isLocked && inventory.length > 0 && (
        <div className="flex justify-end">
          <Button 
            onClick={lockPhase} 
            disabled={locking}
            className="bg-sky-600 hover:bg-sky-700 text-white"
            data-testid="lock-phase-btn"
          >
            {locking ? (
              <>
                <div className="h-4 w-4 mr-2 animate-spin rounded-full border-2 border-white border-t-transparent" />
                Locking...
              </>
            ) : (
              <>
                <Lock className="h-4 w-4 mr-2" />
                Lock Phase 2 & Proceed to LCIA
              </>
            )}
          </Button>
        </div>
      )}

      {/* Unlock Button when locked */}
      {isLocked && (
        <div className="flex justify-end">
          <Button 
            variant="outline"
            onClick={handleUnlock} 
            disabled={unlocking}
            className="border-amber-500 text-amber-600 hover:bg-amber-50"
            data-testid="unlock-phase2-btn"
          >
            {unlocking ? (
              <>
                <div className="h-4 w-4 mr-2 animate-spin rounded-full border-2 border-amber-500 border-t-transparent" />
                Unlocking...
              </>
            ) : (
              <>
                <Unlock className="h-4 w-4 mr-2" />
                Unlock & Edit Inventory
              </>
            )}
          </Button>
        </div>
      )}

      {/* Summary */}
      {inventory.length > 0 && (
        <Card className="bg-slate-50 border-slate-200">
          <CardContent className="p-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              {Object.entries(categoryConfig).map(([key, cfg]) => {
                const count = counts[key];
                const CategoryIcon = cfg.icon;
                return (
                  <div key={key} className="p-3 bg-white rounded-lg border border-slate-200">
                    <CategoryIcon className={`h-5 w-5 mx-auto mb-1 ${cfg.color.split(' ')[1]}`} />
                    <div className="text-2xl font-bold text-slate-900">{count}</div>
                    <div className="text-xs text-slate-500">{cfg.label}</div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
